import { SEGMENTS, MILESTONES, TOTAL_MILES } from "../../shared/route/index.ts";
import type { Activity, ProgressSummary } from "../lib/api";
import { fmt, conv, fmtMiles, fmtMeters, fmtDate, type Units } from "../lib/format";
import { Icon } from "./Icons";

const LABELS: Record<string, string> = { walk: "Walk", run: "Run", hike: "Hike", treadmill: "Treadmill", row: "Row", cycle: "Cycle", elliptical: "Elliptical", other: "Other" };
const MAJORS = MILESTONES.filter((m) => m.major && m.mile > 0);

interface Props {
  progress: ProgressSummary;
  activities: Activity[];
  units: Units;
  onDelete: (id: string) => void;
}

export default function JourneyScreen({ progress, activities, units, onDelete }: Props) {
  const total = progress.totalMiles;
  const reachedMajors = MAJORS.filter((m) => total >= m.mile).length;
  const shown = activities.slice(0, 40);

  return (
    <div className="screen" aria-label="Journey log">
      <div className="screen-inner">
        <h1 className="h1">The Journey</h1>
        <div className="sub">{fmtMiles(total, units)} of {fmtMiles(TOTAL_MILES, units)} · {reachedMajors} of {MAJORS.length} major milestones</div>

        {SEGMENTS.map((leg) => {
          const walked = Math.max(0, Math.min(total, leg.endMile) - leg.startMile);
          const pct = Math.max(0, Math.min(1, walked / leg.miles));
          const ms = MAJORS.filter((m) => m.mile > leg.startMile && m.mile <= leg.endMile);
          return (
            <section className="card" key={leg.id}>
              <div className="card-head">
                <div>
                  <div className="overline" style={{ fontSize: 11.5, letterSpacing: ".16em" }}>Leg {leg.order}{leg.order === 8 ? " · Bonus" : ""}</div>
                  <div className="leg-name">{leg.name}</div>
                </div>
                <div className="leg-dist">{fmtMiles(leg.miles, units)}</div>
              </div>
              <div className="bar"><div style={{ width: `${Math.round(pct * 100)}%` }} /></div>
              <div className="bar-label">{pct >= 1 ? "Complete" : pct <= 0 ? "Not yet begun" : `${fmt(conv(walked, units))} of ${fmtMiles(leg.miles, units)}`}</div>
              <div className="ms-list">
                {ms.map((m) => {
                  const lit = total >= m.mile;
                  return (
                    <div className="ms-row" key={m.mile + m.title}>
                      <div className={lit ? "ms-dot lit" : "ms-dot"} />
                      <div className={lit ? "ms-body lit" : "ms-body"}>
                        <div className="ms-name">{m.mapLabel ?? m.title}</div>
                        <div className="ms-mile">mile {Math.round(m.mile).toLocaleString("en-US")}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        <h2 className="h2">Activity log</h2>
        {activities.length === 0 && <div className="sub">No outings logged yet.</div>}
        {shown.map((a) => (
          <div className="act-row" key={a.id}>
            <Icon id={`act-${LABELS[a.activity_type] ? a.activity_type : "other"}`} size={20} className="icon" />
            <div className="act-main">
              <div className="act-label">{LABELS[a.activity_type] ?? "Other"}{a.source !== "manual" ? ` · ${a.source}` : ""}</div>
              <div className="act-sub">{fmtDate(a.occurred_on)}{a.duration_seconds ? ` · ${Math.round(a.duration_seconds / 60)} min` : ""}{a.notes ? ` · ${a.notes}` : ""}</div>
            </div>
            <div className="act-dist">{fmtMeters(a.distance_meters, units)}</div>
            <button className="act-del" aria-label="Delete entry" onClick={() => onDelete(a.id)}>✕</button>
          </div>
        ))}
        {activities.length > 40 && <div className="sub small" style={{ marginTop: 10 }}>… and {activities.length - 40} earlier entries</div>}
      </div>
    </div>
  );
}
